import React, { useState } from "react";
import { useLazyQuery, gql } from "@apollo/client";

// "What if" for a leave fine setting: re-prices a past month's approved and
// absent leaves with a different value, without saving it. Both columns are
// recomputed from today's data, so the difference is the setting's effect
// alone.

const SIMULATE_CONFIG_CHANGE = gql`
  query SimulateConfigChange($data: SimulateConfigChangeInput!) {
    simulateConfigChange(data: $data) {
      status
      message
      result {
        key
        label
        currentValue
        newValue
        itemsConsidered
        currentTotal
        simulatedTotal
        difference
        peopleAffected
        rows {
          employeeId
          fullName
          current
          simulated
          difference
        }
      }
    }
  }
`;

// Settings the simulation understands (config-what-if.service.js)
export const WHAT_IF_KEYS = [
  "ADVANCE_LEAVE_DEDUCTION",
  "EMERGENCY_LEAVE_APPROVED_DEDUCTION",
  "EMERGENCY_LEAVE_UNAPPROVED_DEDUCTION",
  "WEEKEND_LEAVE_APPROVED_DEDUCTION",
  "WEEKEND_LEAVE_UNAPPROVED_DEDUCTION",
  "RESTRICTED_LEAVE_APPROVED_DEDUCTION",
  "RESTRICTED_LEAVE_UNAPPROVED_DEDUCTION",
  "LEAVE_ADVANCE_NOTICE_DAYS",
  "LEAVE_CONSECUTIVE_THRESHOLD",
  "LEAVE_EXTENDED_MULTIPLIER",
  "PROBATION_PERIOD_DAYS",
  "RETRAINING_FEE",
  "LEAVE_RETRAINING_THRESHOLD",
  "MONTHLY_FINE_CAP_PERCENT",
];

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const signed = (n) => (n > 0 ? `+${inr(n)}` : n < 0 ? `-${inr(-n)}` : inr(0));

const WhatIfModal = ({ config, onClose }) => {
  const last = new Date();
  last.setMonth(last.getMonth() - 1);
  const [month, setMonth] = useState(last.getMonth() + 1);
  const [year, setYear] = useState(last.getFullYear());
  const [value, setValue] = useState("");
  const [run, { data, loading }] = useLazyQuery(SIMULATE_CONFIG_CHANGE, { fetchPolicy: "network-only" });
  const res = data?.simulateConfigChange;
  const result = res?.result;

  const simulate = () => {
    if (value === "" || Number.isNaN(Number(value))) {
      alert("Enter a number");
      return;
    }
    run({ variables: { data: { key: config.key, value: Number(value), month: Number(month), year: Number(year) } } });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-1">
          <h2 className="text-lg font-semibold text-gray-800">What if · {config.label || config.key}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl leading-none">
            ×
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Re-prices a past month's leaves with a different value. Nothing is saved. Now:{" "}
          <span className="font-mono">{typeof config.valueJson === "object" ? JSON.stringify(config.valueJson) : String(config.valueJson)}</span>
          {config.unit ? ` ${config.unit}` : ""}
          {config.key === "LEAVE_ADVANCE_NOTICE_DAYS" && " · leave types with their own notice days keep them"}
        </p>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="text-sm text-gray-700">
            New value
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="block border rounded px-2 py-1 w-32 mt-1"
              autoFocus
            />
          </label>
          <label className="text-sm text-gray-700">
            Month
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="block border rounded px-2 py-1 mt-1">
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString("en-IN", { month: "short" })}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Year
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="block border rounded px-2 py-1 w-24 mt-1"
            />
          </label>
          <button
            onClick={simulate}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-semibold disabled:opacity-50"
          >
            {loading ? "Working..." : "Simulate"}
          </button>
        </div>

        {res?.status === "ERROR" && <p className="text-sm text-red-600">{res.message}</p>}
        {result && (
          <>
            <div
              className={`rounded p-3 mb-4 text-sm ${
                result.difference > 0 ? "bg-red-50 text-red-900" : result.difference < 0 ? "bg-green-50 text-green-900" : "bg-gray-50 text-gray-800"
              }`}
            >
              At <b>{result.newValue}</b> instead of <b>{result.currentValue}</b>, deductions would have been{" "}
              <b>{inr(result.simulatedTotal)}</b> instead of <b>{inr(result.currentTotal)}</b> ({signed(result.difference)})
              across <b>{result.peopleAffected}</b> {result.peopleAffected === 1 ? "person" : "people"}.
              <div className="text-xs opacity-70 mt-1">
                {result.key === "MONTHLY_FINE_CAP_PERCENT"
                  ? `${result.itemsConsidered} people with fines that month.`
                  : `${result.itemsConsidered} approved / absent leaves that month.`}
              </div>
            </div>
            {result.rows.length > 0 && (
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-gray-700 border-b">
                  <tr>
                    <th className="py-2 px-3">Professional</th>
                    <th className="py-2 px-3 text-right">Now</th>
                    <th className="py-2 px-3 text-right">With new value</th>
                    <th className="py-2 px-3 text-right">Change</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {result.rows.map((r) => (
                    <tr key={r.employeeId}>
                      <td className="py-2 px-3 text-gray-800">{r.fullName}</td>
                      <td className="py-2 px-3 text-right">{inr(r.current)}</td>
                      <td className="py-2 px-3 text-right">{inr(r.simulated)}</td>
                      <td
                        className={`py-2 px-3 text-right font-semibold ${
                          r.difference > 0 ? "text-red-700" : r.difference < 0 ? "text-green-700" : "text-gray-400"
                        }`}
                      >
                        {signed(r.difference)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WhatIfModal;
